import { type FC } from "react";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  SolutionOutlined,
  TagOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, message, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import { CalendarDays, Dumbbell, HeartPulse, Salad, Utensils } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  clientScheduledTasksActions,
  traineeActivitiesActions,
  traineeMeasurementsActions,
} from "@fitness-app/app-store";
import { ActionType, EVENT_TYPE, type CalendarEvent } from "@fitness-app/data-models/domain/services/ProgramService";
import { TaskType } from "@fitness-app/data-models/entities/Automation";
import { TemplateType } from "@fitness-app/data-models/entities/AutomationTemplate";
import { CalendarEventStatus } from "@fitness-app/data-models/entities/CalendarEvent";
import { ExerciseRecordingStatus } from "@fitness-app/data-models/entities/ExerciseRecording";
import {
  type WorkoutActivityHealthExportMetadata,
  type WorkoutActivityHealthMetadata,
} from "@fitness-app/data-models/entities/ProgramActivity";
import {
  ScheduledNutritionType,
  type ProgramAutomationTask,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import { TrainingStatus } from "@fitness-app/data-models/entities/TrainingProgram";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { SurveyAutomationItem } from "~/modules/Automation/ProgramAutomation/components/AddSurveyTemplateForm/SurveyAutomationItem";
import EmailClientTaskItem from "~/modules/Automation/ProgramAutomation/components/EmailClientTaskItem/EmailClientTaskItem";
import FitnessAppTaskItem from "~/modules/Automation/ProgramAutomation/components/FitnessAppTaskItem/FitnessAppTaskItem";
import AiAgentMessageItem from "~/modules/Automation/ProgramAutomation/components/AiAgentMessageItem/AiAgentMessageItem";
import AiReportItem from "~/modules/Automation/ProgramAutomation/components/AiReportItem/AiReportItem";
import MessageAutomationItem from "~/modules/Automation/ProgramAutomation/components/MessageAutomationItem/MessageAutomationItem";
import PopUpAutomationItem from "~/modules/Automation/ProgramAutomation/components/PopUpAutomationItem/PopUpAutomationItem";
import TeamMemberTaskItem from "~/modules/Automation/ProgramAutomation/components/TeamMemberTaskItem/TeamMemberTaskItem";
import { mapTrainingStatusToBadge } from "~/modules/Trainee/components/TraineeStatusBadge/mappers";
import {
  CalendarEventStatusBadge,
  calendarEventStatusLabels,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/CalendarEventStatusBadge";
import MeasurementCalendarEvent from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/MeasurementCalendarEvent";
import { useAppDispatch } from "~/store/initializeStore";
import { useUpdateCalendarEvent } from "../hooks/useUpdateCalendarEventStatus";
import { formatLongDay } from "~/components/EventCalendar/calendarModel";
import {
  DetailRow,
  EventPopover,
  EventPopoverCard,
  PopoverSection,
  StatusPill,
  type PillTone,
} from "~/components/EventCalendar/EventPopover";
import { LinkedHealthWorkout } from "./EventCalendar/LinkedHealthWorkout";
import { NutritionMacros } from "./EventCalendar/NutritionMacros";
import { WorkoutExerciseList } from "./EventCalendar/WorkoutExerciseList";
import { MeetingUrlField } from "./MeetingUrlField";

type WorkoutBadgeStatus = (typeof mapTrainingStatusToBadge)[TrainingStatus];

const WORKOUT_STATUS_DOT: Record<WorkoutBadgeStatus, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  warning: "bg-amber-500",
  processing: "bg-blue-500",
};

const WORKOUT_STATUS_PILL: Record<WorkoutBadgeStatus, PillTone> = {
  success: "green",
  error: "red",
  warning: "amber",
  processing: "blue",
};

const CALENDAR_EVENT_STATUS_PILL: Record<CalendarEventStatus, PillTone> = {
  [CalendarEventStatus.ACTIVE]: "blue",
  [CalendarEventStatus.CANCELED]: "red",
  [CalendarEventStatus.NO_SHOW]: "amber",
  [CalendarEventStatus.ATTENDED]: "green",
};

const HEALTH_PROVIDER_LABELS: Record<string, string> = {
  "apple-health": "Apple Health",
  "health-connect": "Health Connect",
  "wear-os": "Wear OS",
  unsupported: "Brak integracji na tym telefonie",
};

const HEALTH_EXPORT_REASON_LABELS: Record<string, string> = {
  unavailable: "Integracja niedostępna na telefonie",
  "not-authorized": "Brak zgody na zapis treningów",
  "invalid-workout": "Niepoprawne dane treningu",
  "replaced-by-watch": "Zastąpiony treningiem z zegarka",
};

const HEALTH_EXPORT_STATUS_LABELS: Record<WorkoutActivityHealthExportMetadata["status"], string> = {
  exported: "Wysłano",
  failed: "Błąd wysyłki",
  pending: "Wysyłanie",
  skipped: "Pominięto",
};

const HEALTH_EXPORT_STATUS_PILL: Record<WorkoutActivityHealthExportMetadata["status"], PillTone> = {
  exported: "green",
  failed: "red",
  pending: "blue",
  skipped: "gray",
};

const formatHealthProvider = (provider?: string | null) =>
  provider ? (HEALTH_PROVIDER_LABELS[provider] ?? provider) : "-";
const formatDateTime = (value?: string | null) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-");
const formatNullableNumber = (value?: number | null) =>
  typeof value === "number" ? Intl.NumberFormat("pl-PL").format(value) : "-";

const HealthExportDetails = ({ exportMetadata }: { exportMetadata: WorkoutActivityHealthExportMetadata }) => (
  <>
    <DetailRow label="Eksport">
      <StatusPill tone={HEALTH_EXPORT_STATUS_PILL[exportMetadata.status]}>
        {HEALTH_EXPORT_STATUS_LABELS[exportMetadata.status]}
      </StatusPill>
    </DetailRow>
    <DetailRow label="Aplikacja">{formatHealthProvider(exportMetadata.provider)}</DetailRow>
    {exportMetadata.exportedAt && <DetailRow label="Wysłano">{formatDateTime(exportMetadata.exportedAt)}</DetailRow>}
    {typeof exportMetadata.energyBurnedKcal === "number" && (
      <DetailRow label="Kalorie">{formatNullableNumber(exportMetadata.energyBurnedKcal)} kcal</DetailRow>
    )}
    {exportMetadata.reason && (
      <DetailRow label="Powód">{HEALTH_EXPORT_REASON_LABELS[exportMetadata.reason] ?? exportMetadata.reason}</DetailRow>
    )}
    {exportMetadata.error && <p className="m-0 text-xs text-red-600">{exportMetadata.error}</p>}
  </>
);

const HealthExportSection = ({ health }: { health?: WorkoutActivityHealthMetadata }) => {
  if (!health?.export) {
    return null;
  }

  return (
    <PopoverSection label="Eksport do Zdrowia">
      <HealthExportDetails exportMetadata={health.export} />
    </PopoverSection>
  );
};

const hasHealthSyncSignal = (health?: WorkoutActivityHealthMetadata) =>
  Boolean(health?.linkedWorkout || (health?.export && health.export.status !== "skipped"));

const getHealthSyncIconClassName = (health?: WorkoutActivityHealthMetadata) => {
  if (health?.export?.status === "failed") {
    return "text-red-600";
  }

  if (health?.export?.status === "pending") {
    return "text-blue-500";
  }

  return "text-rose-500";
};

const getHealthSyncTooltip = (health?: WorkoutActivityHealthMetadata) => {
  if (health?.linkedWorkout && health.export?.status === "exported") {
    return "Powiązano trening z telefonu i wysłano do Fitness";
  }

  if (health?.linkedWorkout) {
    return "Powiązano trening z telefonu";
  }

  if (health?.export?.status === "exported") {
    return "Trening wysłany do Fitness";
  }

  if (health?.export?.status === "pending") {
    return "Trening jest wysyłany do Fitness";
  }

  if (health?.export?.status === "failed") {
    return "Błąd wysyłki treningu do Fitness";
  }

  return "Integracja Fitness";
};

const CustomEventCell: FC<{ event: CalendarEvent }> = ({ event }) => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const {
    mutation: { isLoading: isUpdatingStatus, mutate: updateCalendarEventStatus },
  } = useUpdateCalendarEvent();

  const removeScheduledProgramTask = async (taskId: string, traineeId?: string) => {
    void message.loading({ content: "messages.loading.deleting", duration: 0, key: "remove" });
    await dispatch(clientScheduledTasksActions.removeClientTask(taskId));
    message.destroy("remove");
    if (traineeId) {
      queryClient.invalidateQueries(["scheduledMessageTasksModal", traineeId]);
      queryClient.invalidateQueries(["scheduledMessageTasks"]);
    }
  };

  const setSelectedTask = (task: ProgramAutomationTask) => {
    dispatch(clientScheduledTasksActions.setSelectedTask(task));
  };

  if (event.type === EVENT_TYPE.AUTOMATION_TASK) {
    if (event.data.task.type === TemplateType.ScheduledPopUp) {
      return (
        <div className="flex min-w-0 items-center gap-x-2">
          <TagOutlined className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">Kampania: {event.data.task.name}</span>
        </div>
      );
    }

    if (event.data.task.type === ScheduledNutritionType.ByCurrentWeight) {
      return (
        <div className="flex min-w-0 items-center gap-x-2">
          <Utensils color="rgb(38, 153, 251)" className="shrink-0" size={14} />
          <span className="min-w-0 flex-1 truncate">Dodanie zaplanowanej diety</span>
        </div>
      );
    }

    if (event.data.task.type === ScheduledNutritionType.AssignedByTrainer) {
      return (
        <EventPopover
          content={
            <EventPopoverCard
              icon={<Utensils />}
              tone="sky"
              title="Dodanie diety"
              subtitle={formatLongDay(dayjs(event.start))}
            >
              <DetailRow label="Plan żywieniowy">{event.data.task.mealsPlanName}</DetailRow>
            </EventPopoverCard>
          }
        >
          <div className="flex min-w-0 items-center gap-x-2">
            <Utensils color="rgb(38, 153, 251)" className="shrink-0" size={14} />
            <span className="min-w-0 flex-1 truncate">Dodanie diety</span>
          </div>
        </EventPopover>
      );
    }

    if (event.data.task.type === TemplateType.ScheduledMessages) {
      return (
        <MessageAutomationItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id, event.data.traineeId)}
          sentDate={event.data.sendDate}
        />
      );
    }

    if (event.data.task.type === TemplateType.ScheduledAiAgentMessage) {
      return (
        <AiAgentMessageItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id, event.data.traineeId)}
          sentDate={event.data.sendDate}
        />
      );
    }

    if (event.data.task.type === TemplateType.ScheduledAiReport) {
      return (
        <AiReportItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id, event.data.traineeId)}
          sentDate={event.data.sendDate}
        />
      );
    }

    if (event.data.task.type === TemplateType.ScheduledPopUpFromAutomation) {
      return (
        <PopUpAutomationItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id, event.data.traineeId)}
          sentDate={event.data.sendDate}
        />
      );
    }

    if (event.data.task.type === TemplateType.ScheduledSurvey) {
      return (
        <SurveyAutomationItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id)}
          onEdit={() => setSelectedTask(event.data)}
          sentDate={event.data.sendDate}
        />
      );
    }

    if (event.data.task.type === TaskType.EmailClient) {
      return (
        <EmailClientTaskItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id)}
        />
      );
    }

    if (event.data.task.type === TaskType.FitnessApp) {
      return (
        <FitnessAppTaskItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id)}
        />
      );
    }

    if (event.data.task.type === TaskType.TeamMemberTask) {
      return (
        <TeamMemberTaskItem
          item={event.data.task}
          status={event.data.status}
          onRemove={() => removeScheduledProgramTask(event.data.id)}
        />
      );
    }
  }

  if (event.type === EVENT_TYPE.MEASUREMENT) {
    return <MeasurementCalendarEvent measurement={event.data} />;
  }

  if (event.type === ActionType.StartOfCurrenPlan) {
    return (
      <div className="block overflow-hidden text-ellipsis whitespace-nowrap">
        <SolutionOutlined style={{ color: "#2699fb" }} /> <span>Początek obecnego planu</span>
      </div>
    );
  }

  // TRE-1826 — bez tego dwa zaplanowane plany pod rząd wyglądają w kalendarzu jak jeden
  // ciąg treningów i nie widać dnia, w którym klientka przechodzi na kolejny.
  if (event.type === ActionType.StartOfScheduledPlan) {
    return (
      <Tooltip title={`Start zaplanowanego planu: ${event.data.name}`}>
        <div className="flex min-w-0 items-center gap-x-2 font-medium text-blue-500">
          <SolutionOutlined className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">Początek: {event.data.name}</span>
        </div>
      </Tooltip>
    );
  }

  if (event.type === EVENT_TYPE.MEAL_TRACKED) {
    return (
      <EventPopover
        content={
          <EventPopoverCard
            icon={<Salad />}
            tone="rose"
            title="Posiłki"
            subtitle={formatLongDay(dayjs(event.start))}
            footer={
              <Button size="small" onClick={() => dispatch(traineeMeasurementsActions.selectNutritionDay(event.data))}>
                Pokaż szczegóły
              </Button>
            }
          >
            <NutritionMacros calories={event.data.calories} macros={event.data.macros} targets={event.data.targets} />
          </EventPopoverCard>
        }
      >
        <div className="flex min-w-0 items-center gap-x-2">
          <Salad size={14} className="shrink-0" color="#f43f5e" />
          <span className="min-w-0 flex-1 truncate">Posiłki</span>
        </div>
      </EventPopover>
    );
  }

  // TRE-1826 — trening z planu ZAPLANOWANEGO. Bez statusu, bez nagrań i bez „Pokaż
  // szczegóły": ten dzień jeszcze nie istnieje jako aktywność, a edycja z kalendarza
  // przypięłaby go do planu aktywnego.
  if (event.type === EVENT_TYPE.WORKOUT && event.metadata.readOnly) {
    return (
      <EventPopover
        content={
          <EventPopoverCard
            icon={<Dumbbell />}
            tone="gray"
            title={event.title}
            subtitle={formatLongDay(dayjs(event.start))}
            badge={<StatusPill tone="gray">Zaplanowany plan</StatusPill>}
          >
            <WorkoutExerciseList workout={event.data} showProgress={false} />
          </EventPopoverCard>
        }
      >
        {/*
         * Ikona zamiast tagu z napisem: komórka dnia potrafi mieć ~120 px, a antd Tag
         * zjadał połowę tej szerokości i wypychał tytuł poza kadr. Prawą stronę tej
         * karty i tak trzymają ikonki (nagrania, Fitness), więc to spójne z resztą.
         */}
        <div className="flex min-w-0 items-center gap-x-2">
          {/*
           * Kropka i tekst zamiast antd `Badge`: `truncate` musi trafić na prawdziwy węzeł
           * tekstowy, a `Badge` renderuje inline-block, który by się przyciął w połowie
           * zamiast dostać wielokropek.
           */}
          <span className="size-1.5 shrink-0 rounded-full bg-gray-300" />
          <span className="min-w-0 flex-1 truncate text-gray-400 italic">{event.title}</span>
          <Tooltip title="Trening z zaplanowanego planu — jeszcze się nie zaczął">
            <ClockCircleOutlined className="shrink-0 text-blue-400" />
          </Tooltip>
        </div>
      </EventPopover>
    );
  }

  if (event.type === EVENT_TYPE.WORKOUT) {
    const hasAnyRecordings = !!event.metadata.requiredRecordings?.length;
    const hasAnyRecorded = event.metadata.requiredRecordings?.some((recording) =>
      [ExerciseRecordingStatus.RECORDED, ExerciseRecordingStatus.RATED].includes(recording.status),
    );
    const hasHealthSync = hasHealthSyncSignal(event.metadata.health);
    const status = event.metadata.status || TrainingStatus.NEW;
    const recordings = event.metadata.requiredRecordings ?? [];
    const recordedCount = recordings.filter((recording) =>
      [ExerciseRecordingStatus.RECORDED, ExerciseRecordingStatus.RATED].includes(recording.status),
    ).length;

    return (
      <EventPopover
        content={
          <EventPopoverCard
            icon={<Dumbbell />}
            tone="indigo"
            title={event.title}
            subtitle={formatLongDay(dayjs(event.start))}
            badge={
              <StatusPill tone={WORKOUT_STATUS_PILL[mapTrainingStatusToBadge[status]]}>
                {t(`common:trainingStatus.${status.toLowerCase()}`)}
              </StatusPill>
            }
            footer={
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  dispatch(
                    traineeActivitiesActions.setCurrentDayWorkout({
                      workout: event.data,
                      date: dayjs(event.start).format("YYYY-MM-DD"),
                      source: event.metadata.source,
                      id: event.metadata.id ?? null,
                      status: event.metadata.status ?? null,
                      recordings: event.metadata.requiredRecordings ?? null,
                    }),
                  );
                  dispatch(traineeActivitiesActions.setWorkoutEditModalShown());
                }}
              >
                Otwórz trening
              </Button>
            }
          >
            {event.metadata.health?.linkedWorkout && (
              <LinkedHealthWorkout linkedWorkout={event.metadata.health.linkedWorkout} />
            )}
            <WorkoutExerciseList workout={event.data} />
            {recordings.length > 0 && (
              <PopoverSection label="Nagrania">
                <DetailRow label="Nagrane ćwiczenia">
                  {recordedCount} z {recordings.length}
                </DetailRow>
              </PopoverSection>
            )}
            <HealthExportSection health={event.metadata.health} />
          </EventPopoverCard>
        }
      >
        <div className="flex min-w-0 items-center gap-x-1.5">
          <span
            className={cn(
              "size-1.5 shrink-0 rounded-full",
              WORKOUT_STATUS_DOT[mapTrainingStatusToBadge[event.metadata.status || TrainingStatus.NEW]],
            )}
          />
          <span className="min-w-0 flex-1 truncate">{event.title}</span>
          <div className="flex shrink-0 items-center gap-x-1">
            {hasHealthSync && (
              <Tooltip title={getHealthSyncTooltip(event.metadata.health)}>
                <HeartPulse
                  size={16}
                  className={cn("align-text-top", getHealthSyncIconClassName(event.metadata.health))}
                />
              </Tooltip>
            )}
            {hasAnyRecordings ? (
              hasAnyRecorded ? (
                <VideoCameraOutlined className="text-green-600" />
              ) : (
                <VideoCameraAddOutlined className="text-amber-600" />
              )
            ) : null}
          </div>
        </div>
      </EventPopover>
    );
  }

  if (event.type === EVENT_TYPE.CALENDAR_EVENT) {
    return (
      <EventPopover
        content={
          <EventPopoverCard
            icon={<CalendarDays />}
            tone="emerald"
            title="Konsultacje online"
            subtitle={`${formatLongDay(dayjs(event.start))}, ${dayjs(event.start).format("HH:mm")} - ${dayjs(event.end).format("HH:mm")}`}
            badge={
              <StatusPill tone={CALENDAR_EVENT_STATUS_PILL[event.data.status]}>
                {calendarEventStatusLabels[event.data.status]}
              </StatusPill>
            }
          >
            <div className="flex flex-col gap-2.5">
              {event.data.eventData.scheduled_event.name && (
                <DetailRow label="Opis">{event.data.eventData.scheduled_event.name}</DetailRow>
              )}
              <DetailRow label="Status">
                <Select
                  size="small"
                  className="w-full"
                  value={event.data.status}
                  loading={isUpdatingStatus}
                  onChange={(value) => {
                    updateCalendarEventStatus({
                      eventId: event.data.id,
                      update: { status: value, traineeId: event.data.traineeId || undefined },
                    });
                  }}
                  options={Object.values(CalendarEventStatus).map((status) => ({
                    value: status,
                    label: <CalendarEventStatusBadge status={status} />,
                  }))}
                />
              </DetailRow>
              <DetailRow label="Link">
                <MeetingUrlField event={event.data} source="trainee_calendar" width="100%" />
              </DetailRow>
            </div>
          </EventPopoverCard>
        }
      >
        <div className="flex min-w-0 items-center gap-x-2">
          <CalendarOutlined
            className={cn("text-blue-500", {
              "text-red-500": event.data.status === CalendarEventStatus.CANCELED,
              "text-green-500": event.data.status === CalendarEventStatus.ATTENDED,
              "text-amber-500": event.data.status === CalendarEventStatus.NO_SHOW,
            })}
          />
          <span className="min-w-0 flex-1 truncate">{dayjs(event.start).format("HH:mm")} Konsultacje</span>
        </div>
      </EventPopover>
    );
  }

  return <span>{event.title}</span>;
};

export default CustomEventCell;
